/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CdrFieldsBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the CDR Settings module.
----------------------------------------------------------------*/


import { useCallback } from 'react';
import {
    DndContext, PointerSensor, KeyboardSensor, closestCenter,
    useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { List, Plus } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Button } from '../../../components/ui/button';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { CdrSortableFieldRow } from './CdrSortableFieldRow';
import { useI18n } from '../../../hooks/useI18n';
import type { CdrField } from '../types';

interface Props {
    fields: CdrField[];
    isLoading: boolean;
    onFieldChange: (index: number, updated: Partial<CdrField>) => void;
    onRemoveField: (index: number) => void;
    onAddField: () => void;
    onDragEnd: (reordered: CdrField[]) => void;
}

function getFieldKey(field: CdrField, index: number): string {
    return field.id ? `field-${field.id}` : field.tempId ?? `temp-${index}`;
}

export function CdrFieldsBox({ fields, isLoading, onFieldChange, onRemoveField, onAddField, onDragEnd }: Props) {
    const { t } = useI18n();

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = fields.findIndex((f, i) => getFieldKey(f, i) === active.id);
        const newIndex = fields.findIndex((f, i) => getFieldKey(f, i) === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        onDragEnd(arrayMove(fields, oldIndex, newIndex));
    }, [fields, onDragEnd]);

    return (
        <FormBox title={t('cdrSettings.sections.fields')} description={t('cdrSettings.description')} icon={<List className="h-5 w-5" />} isLoading={isLoading}>
            <div className="overflow-x-auto">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <table className="w-full min-w-[760px] table-auto border-spacing-y-2">
                        <thead>
                            <tr className="text-left text-xs uppercase text-muted-foreground">
                                <th className="w-28 px-2 py-2 text-center">
                                    <SimpleTooltip content={t('cdrSettings.tooltips.fieldSortOrder')} position="top" maxWidth="260px">
                                        <span>{t('cdrSettings.fields.fieldSortOrder.label')}</span>
                                    </SimpleTooltip>
                                </th>
                                <th className="px-2 py-2">
                                    <SimpleTooltip content={t('cdrSettings.tooltips.fieldVarName')} position="top" maxWidth="300px">
                                        <span>{t('cdrSettings.fields.fieldVarName.label')}</span>
                                    </SimpleTooltip>
                                </th>
                                <th className="px-2 py-2">
                                    <SimpleTooltip content={t('cdrSettings.tooltips.fieldAliasName')} position="top" maxWidth="300px">
                                        <span>{t('cdrSettings.fields.fieldAliasName.label')}</span>
                                    </SimpleTooltip>
                                </th>
                                <th className="px-2 py-2 text-center">
                                    <SimpleTooltip content={t('cdrSettings.tooltips.fieldQuote')} position="top" maxWidth="300px">
                                        <span>{t('cdrSettings.fields.fieldQuote.label')}</span>
                                    </SimpleTooltip>
                                </th>
                                <th className="px-2 py-2 text-center">
                                    <SimpleTooltip content={t('cdrSettings.tooltips.fieldEnabled')} position="top" maxWidth="300px">
                                        <span>{t('cdrSettings.fields.fieldEnabled.label')}</span>
                                    </SimpleTooltip>
                                </th>
                                <th className="px-2 py-2 text-right">{t('cdrSettings.table.actions')}</th>
                            </tr>
                        </thead>

                        <SortableContext items={fields.map((f, i) => getFieldKey(f, i))} strategy={verticalListSortingStrategy}>
                            <tbody>
                                {fields.map((field, index) => (
                                    <CdrSortableFieldRow
                                        key={getFieldKey(field, index)}
                                        id={getFieldKey(field, index)}
                                        index={index}
                                        field={field}
                                        onChange={(updated) => onFieldChange(index, updated)}
                                        onRemove={() => onRemoveField(index)}
                                    />
                                ))}
                                {fields.length === 0 && !isLoading ? (
                                    <tr><td colSpan={6} className="px-2 py-6 text-center text-sm text-muted-foreground">{t('common.noData')}</td></tr>
                                ) : null}
                            </tbody>
                        </SortableContext>
                    </table>
                </DndContext>
            </div>
            <div className="mt-4 flex justify-end">
                <Button type="button" onClick={onAddField} variant="outline">
                    <Plus className="mr-2 h-4 w-4" />{t('cdrSettings.actions.addField')}
                </Button>
            </div>
        </FormBox>
    );
}
